import KUTE from "kute.js";
import KeenSlider from "keen-slider";
import Layer from "./Layer.js";
import { CACHE_MANAGER } from "../utils.js";

/**
 *
 * @param {Spinner} spinner
 * @param {HTMLElement} node
 * @param {HTMLElement} [subjectNode]
 * @class
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function CarouselLayer(spinner, node, subjectNode) {
  Layer.call(this, spinner, node, subjectNode);
  this.container = this.host.parentNode;
  this.thumbnails = [];
  this.slider = new KeenSlider(this.subject, {
    loop: true,
    selector: ".slide",
  });

  const elById = (id) => {
    return this.host.querySelector(`#${id}`);
  };

  this.nodeThumbnails = elById("thumbnails");
  this.nodePrevSlide = elById("prev-slide");
  this.nodeNextSlide = elById("next-slide");
  this.nodeCloseModal = elById("close-modal");

  this.slider.on("slideChanged", (e) => {
    this.activeThumbnailIndex = e.track.details.rel;
  });

  this.slider.on("optionsChanged", (e) => {
    this.activeThumbnailIndex = e.track.details.rel;
  });

  this.nodePrevSlide.addEventListener("pointerup", () => {
    this.slider.prev();
  });

  this.nodeNextSlide.addEventListener("pointerup", () => {
    this.slider.next();
  });

  this.nodeCloseModal.addEventListener("pointerup", this.close.bind(this));

  this.container.addEventListener("pointerup", (event) => {
    if (event.target !== this.subject) return;

    this.close();
  });

  spinner.on("keydown", (event) => {
    if (!this.isOpen) {
      return;
    }

    try {
      switch (event.key) {
        case "Escape":
          this.close();
          break;
        case "ArrowLeft":
          this.slider.prev();
          break;
        case "ArrowRight":
          this.slider.next();
          break;
      }
    } catch {
      console.warn("Slider not ready yet!");
    }
  });
}

CarouselLayer.prototype = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __proto__: Layer.prototype,

  get isOpen() {
    return this.host.style.display !== "none";
  },

  set activeThumbnailIndex(value) {
    if (this.oldThumbnail) {
      this.oldThumbnail.classList.remove("active");
    }

    this.oldThumbnail = this.thumbnails[value];
    this.oldThumbnail.classList.add("active");
    this.nodeThumbnails.scrollTo({
      left:
        this.oldThumbnail.offsetLeft +
        this.oldThumbnail.offsetWidth / 2 -
        this.nodeThumbnails.offsetWidth / 2,
      behavior: "smooth",
    });
  },

  set sliderThumbnailsVisibility(value) {
    if (value) {
      KUTE.fromTo(
        this.nodeThumbnails,
        { translateY: 100 },
        { translateY: 0 },
        {
          delay: value,
          duration: 500,
          easing: "easingQuinticOut",
        },
      ).start();
    } else {
      this.nodeThumbnails.style.transform = "translateY(100px)";
    }
  },

  set sliderArrowsVisibility(value) {
    if (value) {
      this.nodePrevSlide.style.display = "block";
      this.nodeNextSlide.style.display = "block";
    } else {
      this.nodePrevSlide.style.display = "none";
      this.nodeNextSlide.style.display = "none";
    }
  },

  clear() {
    this.subject.innerHTML = "";
    this.nodeThumbnails.innerHTML = "";
    this.thumbnails = [];
  },

  /**
   *
   * @param {HTMLElement} slideContainer
   * @param {HTMLElement} slideThumbnail
   */
  addSlide(slideContainer, slideThumbnail) {
    this.subject.appendChild(slideContainer);
    this.nodeThumbnails.appendChild(slideThumbnail);
    this.thumbnails.push(slideThumbnail);
  },

  expandFrom(originRect) {
    const spinnerRect = this.container.getBoundingClientRect();
    const originScale = 0.05;
    const originX = originRect.x + originRect.width / 2;
    const originY = originRect.y + originRect.height / 2;
    const originWidth = (spinnerRect.width * originScale) / 2;
    const originHeight = (spinnerRect.height * originScale) / 2;

    KUTE.fromTo(
      this.subject,
      {
        scale: originScale,
        translate: [
          originX - originWidth - spinnerRect.x,
          originY - originHeight - spinnerRect.y,
        ],
      },
      {
        scale: 1,
        translate: [0, 0],
      },
      {
        easing: "easingQuinticOut",
        duration: 500,
        onStart: () => {
          this.subject.style.opacity = "1";
        },
        onComplete: () => {
          this.slider.update(
            {
              loop: true,
              selector: ".slide",
            },
            0,
          );
        },
      },
    ).start();

    this.sliderThumbnailsVisibility = true;
  },

  slideUp() {
    KUTE.fromTo(
      this.subject,
      {
        opacity: 0,
        translateY: 100,
      },
      {
        opacity: 1,
        translateY: 0,
      },
      {
        delay: 200,
        easing: "easingQuinticOut",
        duration: 500,
        onStart: () => {
          this.subject.style.scale = "1";
        },
        onComplete: () => {
          this.slider.update(
            {
              loop: true,
              selector: ".slide",
            },
            0,
          );
        },
      },
    ).start();

    this.sliderThumbnailsVisibility = true;
  },

  ready() {
    this.sliderThumbnailsVisibility = false;
    this.sliderArrowsVisibility = false;
    this.subject.style.opacity = "0";
    this.subject.style.scale = ".05f";
    this.subject.style.transform = "translateY(100px)";

    KUTE.fromTo(
      this.subject.parentNode,
      {
        opacity: 0,
      },
      {
        opacity: 1,
      },
      {
        easing: "easingQuinticOut",
        duration: 200,
        onStart: () => {
          this.subject.parentNode.style.display = "";
        },
      },
    ).start();
  },

  close() {
    KUTE.fromTo(
      this.host,
      {
        opacity: 1,
      },
      {
        opacity: 0,
      },
      {
        duration: 200,
        onComplete: () => {
          this.host.style.display = "none";
        },
      },
    ).start();
  },

  /**
   *
   * @param {{hash: string, row: number, column: number}} data
   * @returns {object}
   */
  createSlide(data) {
    const slideContainer = document.createElement("div");
    slideContainer.className = "slide";

    const slideImage = document.createElement("img");
    slideImage.className = "image-box";
    slideContainer.appendChild(slideImage);

    const slideThumbnail = document.createElement("img");
    slideThumbnail.__slideIndex = this.thumbnails.length;
    slideThumbnail.dataset.slideIndex = `${this.thumbnails.length}`;
    slideThumbnail.onpointerup = (event) => {
      if (event.button === 2) return;

      this.slider.moveToIdx(slideThumbnail.__slideIndex, true, {
        duration: 0,
      });

      if (data.row && data.column) {
        this.setSpinnerRow(data.row);
        this.setSpinnerColumn(data.column);
      }
    };

    slideThumbnail.src = `${this.imagesBaseUrl}/${data.hash}.120`;

    this.addSlide(slideContainer, slideThumbnail);

    return {
      slideContainer,
      slideImage,
      slideThumbnail,
    };
  },

  /**
   *
   * @param {string[] | {hash: string, row: any, column: any}[]} items
   * @returns {Promise<string>|*|Promise<Awaited<*>>|Promise<void>}
   */
  setSlides(items) {
    this.clear();
    let first;

    for (const item of items) {
      const slideData =
        typeof item === "object"
          ? item
          : {
              hash: item,
              column: 0,
              row: 0,
            };

      const { slideImage } = this.createSlide(slideData);

      if (first) {
        slideImage.src = `${this.imagesBaseUrl}/${slideData.hash}.1920`;
      } else {
        first = CACHE_MANAGER.image(
          `${this.imagesBaseUrl}/${slideData.hash}`,
          1920,
        );
        first.then((url) => (slideImage.src = url));
      }
    }

    // Resolve the promise when the first image is loaded
    return first || Promise.resolve();
  },
};

export default CarouselLayer;
